import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { BookOpen, Sparkles, Plus, CheckCircle2, Play } from 'lucide-react';

/**
 * SelleShop — Shoppable Publishing MVP
 * Inventory → Publication (book | catalog) → Publish → Sell → Ship
 * Adapters (publishing / payment / shipping / video) are stubs by design;
 * checkout and shipping flow through existing SelleSend endpoints.
 */

const uid = () => Math.random().toString(36).slice(2, 10);

export default function SelleShopPage() {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [publications, setPublications] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState('products');

  // Product form
  const [pf, setPf] = useState({ title: '', sku: '', price: '', description: '', imageUrl: '', imageSource: 'merchant', inventoryQty: 1 });
  // Publication form
  const [pubf, setPubf] = useState({ type: 'catalog', title: '', requestedPages: 12, contentMode: 'ai-assisted' });

  const log = (event, detail) => setAudit(a => [{ id: uid(), event, detail, createdAt: new Date().toISOString() }, ...a].slice(0, 50));

  const addProduct = (e) => {
    e.preventDefault();
    if (!pf.title || !pf.price) { toast.error('Title + price required'); return; }
    const p = { ...pf, id: uid(), price: parseFloat(pf.price), currency: 'USD', status: 'active', createdAt: new Date().toISOString() };
    setProducts(prev => [p, ...prev]);
    log('product.add', `${p.title} ($${p.price})`);
    setPf({ title: '', sku: '', price: '', description: '', imageUrl: '', imageSource: 'merchant', inventoryQty: 1 });
    toast.success('Product added');
  };

  const markSold = (id) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, status: 'sold' } : p));
    const p = products.find(x => x.id === id);
    log('product.sold', p?.title);
    toast.success('Marked sold');
  };

  const checkoutProduct = async (p) => {
    // Placeholder — in production this creates a Stripe checkout session
    toast.success(`Checkout initiated for ${p.title} — $${p.price}`);
    log('checkout.start', `${p.title} · $${p.price}`);
  };

  const addPublication = (e) => {
    e.preventDefault();
    if (!pubf.title) { toast.error('Title required'); return; }
    const productIds = pubf.type === 'catalog' ? products.filter(p => p.status === 'active').map(p => p.id) : [];
    const pub = { ...pubf, id: uid(), status: 'draft', productIds, createdAt: new Date().toISOString() };
    setPublications(prev => [pub, ...prev]);
    log('publication.create', `${pub.title} · ${pub.type} · ${pub.requestedPages}p`);
    setPubf({ type: 'catalog', title: '', requestedPages: 12, contentMode: 'ai-assisted' });
    toast.success('Publication created');
  };

  const approve = (id) => {
    setPublications(prev => prev.map(p => p.id === id ? { ...p, status: 'approved' } : p));
    log('publication.approve', id);
  };
  const publish = (id) => {
    setPublications(prev => prev.map(p => p.id === id ? { ...p, status: 'available' } : p));
    log('publication.publish', id);
    toast.success('Published (demo)');
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="selleshop-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-indigo-500 font-semibold">Shoppable Publishing</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-1 flex items-center gap-2">
          <BookOpen className="w-8 h-8 text-indigo-500" /> SelleShop™
        </h1>
        <p className="text-slate-500 mt-2 max-w-2xl">
          Turn your inventory into books and shoppable catalogs. Live prices, real inventory, one-tap checkout — powered by SelleSend for payments, protection and shipping.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b" role="tablist">
        {['products','publications','audit'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-500'}`}
            data-testid={`tab-${t}`}
          >{t[0].toUpperCase() + t.slice(1)}</button>
        ))}
      </div>

      {tab === 'products' && (
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Plus className="w-4 h-4" /> Add Product</CardTitle><CardDescription>Live product. Live price. Sold or available in real time.</CardDescription></CardHeader>
            <CardContent>
              <form onSubmit={addProduct} className="grid sm:grid-cols-2 gap-3">
                <div><Label>Title</Label><Input required value={pf.title} onChange={e => setPf({ ...pf, title: e.target.value })} data-testid="product-title" /></div>
                <div><Label>SKU</Label><Input value={pf.sku} onChange={e => setPf({ ...pf, sku: e.target.value })} /></div>
                <div><Label>Price ($)</Label><Input required type="number" step="0.01" value={pf.price} onChange={e => setPf({ ...pf, price: e.target.value })} data-testid="product-price" /></div>
                <div><Label>Qty</Label><Input type="number" min="1" value={pf.inventoryQty} onChange={e => setPf({ ...pf, inventoryQty: parseInt(e.target.value)||1 })} /></div>
                <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={pf.description} onChange={e => setPf({ ...pf, description: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>Image URL</Label><Input value={pf.imageUrl} onChange={e => setPf({ ...pf, imageUrl: e.target.value })} placeholder="https://…" /></div>
                <div className="sm:col-span-2 flex justify-end"><Button type="submit" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button></div>
              </form>
            </CardContent>
          </Card>

          {products.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {products.map(p => (
                <Card key={p.id}>
                  <CardContent className="p-4">
                    {p.imageUrl && <img src={p.imageUrl} alt={p.title} className="w-full h-32 object-cover rounded-md mb-2" />}
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold text-sm">{p.title}</div>
                        <div className="text-xs text-slate-500">{p.sku || '—'} · qty {p.inventoryQty}</div>
                      </div>
                      <div className="text-lg font-bold tabular-nums">${p.price}</div>
                    </div>
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" onClick={() => checkoutProduct(p)} disabled={p.status !== 'active'}><Play className="w-3 h-3 mr-1" /> Checkout</Button>
                      <Button size="sm" variant="ghost" onClick={() => markSold(p.id)} disabled={p.status === 'sold'}>Sold</Button>
                      {p.status === 'sold' && <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-500">Sold</span>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'publications' && (
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-indigo-500" /> New Publication</CardTitle><CardDescription>Book or shoppable catalog. AI-assisted, manual, or fully AI.</CardDescription></CardHeader>
            <CardContent>
              <form onSubmit={addPublication} className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <select className="w-full h-10 px-3 rounded-lg border" value={pubf.type} onChange={e => setPubf({ ...pubf, type: e.target.value })}>
                    <option value="catalog">Shoppable Catalog</option>
                    <option value="book">Book</option>
                  </select>
                </div>
                <div><Label>Title</Label><Input required value={pubf.title} onChange={e => setPubf({ ...pubf, title: e.target.value })} data-testid="pub-title" /></div>
                <div><Label>Pages</Label><Input type="number" min="4" max="500" value={pubf.requestedPages} onChange={e => setPubf({ ...pubf, requestedPages: parseInt(e.target.value)||12 })} /></div>
                <div>
                  <Label>Content mode</Label>
                  <select className="w-full h-10 px-3 rounded-lg border" value={pubf.contentMode} onChange={e => setPubf({ ...pubf, contentMode: e.target.value })}>
                    <option value="ai">Do everything for me (AI)</option>
                    <option value="ai-assisted">AI-assisted</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
                <div className="sm:col-span-2 flex justify-end"><Button type="submit" data-testid="create-pub-btn"><Sparkles className="w-4 h-4 mr-1" /> Create</Button></div>
              </form>
            </CardContent>
          </Card>

          {publications.length > 0 && (
            <div className="space-y-2">
              {publications.map(p => (
                <Card key={p.id}>
                  <CardContent className="p-4 flex justify-between items-center">
                    <div>
                      <div className="font-semibold">{p.title}</div>
                      <div className="text-xs text-slate-500">{p.type} · {p.requestedPages}p · {p.contentMode} · {p.productIds.length} products</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700">{p.status}</span>
                      {p.status === 'draft' && <Button size="sm" variant="outline" onClick={() => approve(p.id)}><CheckCircle2 className="w-3 h-3 mr-1" /> Approve</Button>}
                      {p.status === 'approved' && <Button size="sm" onClick={() => publish(p.id)}>Publish</Button>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'audit' && (
        <Card>
          <CardHeader><CardTitle className="text-base">Audit Trail</CardTitle><CardDescription>Every action, immutable and timestamped.</CardDescription></CardHeader>
          <CardContent>
            {audit.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">No activity yet.</p>
            ) : (
              <div className="space-y-1 max-h-96 overflow-auto text-sm font-mono">
                {audit.map(a => (
                  <div key={a.id} className="flex gap-3 py-1 border-b last:border-b-0">
                    <span className="text-slate-400 text-xs w-40 shrink-0">{new Date(a.createdAt).toLocaleTimeString()}</span>
                    <span className="text-indigo-600 w-32 shrink-0">{a.event}</span>
                    <span className="text-slate-700 truncate">{a.detail}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
